import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCamera, FiMapPin, FiClock, FiInfo, FiX, FiFilter, FiStar } = FiIcons;

const Gallery = ({ standalone = false }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Simulated gallery data with metadata
  const galleryData = [
    {
      id: 1,
      src: 'https://images.unsplash.com/photo-1444927714506-8492d94b5ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      title: 'Serendib Scops Owl',
      category: 'endemic',
      location: 'Lodge Grounds - Evening Terrace',
      time: '18:45',
      date: '2024-02-14',
      camera: 'Sony A1',
      lens: '600mm f/4',
      settings: 'f/5.6 • 1/250s • ISO 3200',
      description: 'Endemic to Sri Lanka. Critically endangered with fewer than 1,000 individuals.',
      rarity: 5,
      scientificName: 'Otus thilohoffmanni'
    },
    {
      id: 2,
      src: 'https://images.unsplash.com/photo-1519904981063-b0cf448d479e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      title: 'Sri Lankan Blue Magpie',
      category: 'endemic',
      location: 'Kudawa Trail - Morning',
      time: '07:20',
      date: '2024-02-15',
      camera: 'Canon R5',
      lens: '400mm f/2.8',
      settings: 'f/4.0 • 1/500s • ISO 800',
      description: 'Endemic passerine bird found in the hill forests of Sri Lanka.',
      rarity: 4,
      scientificName: 'Urocissa ornata'
    },
    {
      id: 3,
      src: 'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      title: 'Red-faced Malkoha',
      category: 'endemic',
      location: 'Photography Hide #2',
      time: '06:15',
      date: '2024-02-16',
      camera: 'Nikon Z9',
      lens: '800mm f/5.6',
      settings: 'f/5.6 • 1/320s • ISO 1600',
      description: 'Large cuckoo endemic to Sri Lanka, found in dense forest canopy.',
      rarity: 4,
      scientificName: 'Phaenicophaeus pyrrhocephalus'
    },
    {
      id: 4,
      src: 'https://images.unsplash.com/photo-1574781330855-d0db170d52dd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      title: 'Sri Lanka Spurfowl',
      category: 'endemic',
      location: 'Forest Floor Trail',
      time: '16:30',
      date: '2024-02-17',
      camera: 'Sony A7R V',
      lens: '200-600mm f/5.6-6.3',
      settings: 'f/6.3 • 1/200s • ISO 2500',
      description: 'Ground-dwelling bird endemic to Sri Lanka, shy and elusive.',
      rarity: 3,
      scientificName: 'Galloperdix bicalcarata'
    },
    {
      id: 5,
      src: 'https://images.unsplash.com/photo-1549366021-9f761d040a94?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      title: 'Oriental Dwarf Kingfisher',
      category: 'resident',
      location: 'Stream near Lodge',
      time: '08:45',
      date: '2024-02-18',
      camera: 'Canon R6 Mark II',
      lens: '500mm f/4',
      settings: 'f/4.0 • 1/640s • ISO 1000',
      description: 'Resident species, excellent fishing skills, prefers shaded streams.',
      rarity: 3,
      scientificName: 'Ceyx erithaca'
    },
    {
      id: 6,
      src: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      title: 'Sri Lanka Frogmouth',
      category: 'endemic',
      location: 'Night Walk Trail',
      time: '20:15',
      date: '2024-02-19',
      camera: 'Sony A1',
      lens: '400mm f/2.8',
      settings: 'f/2.8 • 1/60s • ISO 6400',
      description: 'Nocturnal endemic species with excellent camouflage.',
      rarity: 5,
      scientificName: 'Batrachostomus moniliger'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Species', count: galleryData.length },
    { id: 'endemic', name: 'Endemics', count: galleryData.filter(img => img.category === 'endemic').length },
    { id: 'resident', name: 'Residents', count: galleryData.filter(img => img.category === 'resident').length },
    { id: 'rare', name: 'Ultra Rare', count: galleryData.filter(img => img.rarity === 5).length }
  ];

  const filteredImages = filter === 'all' 
    ? galleryData 
    : filter === 'rare'
    ? galleryData.filter(img => img.rarity === 5)
    : galleryData.filter(img => img.category === filter);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const renderStars = (rarity) => {
    return Array.from({ length: 5 }, (_, i) => (
      <SafeIcon 
        key={i} 
        icon={FiStar} 
        className={`w-4 h-4 ${i < rarity ? 'text-yellow-400 fill-current' : 'text-gray-400'}`} 
      />
    ));
  };

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <p className="text-white text-lg">Loading Endemic Species Gallery...</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`${standalone ? 'pt-32' : 'py-20'} relative overflow-hidden`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="liquid-blob"></div>
        <div className="liquid-blob"></div>
        <div className="liquid-blob"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 glass px-6 py-3 rounded-full mb-6">
            <SafeIcon icon={FiCamera} className="w-5 h-5 text-green-400" />
            <span className="text-white font-medium">High-Resolution Gallery</span>
          </div>
          
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white mb-6">
            Endemic Species
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400">
              Photography Gallery
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Explore our curated collection of endemic bird photography with complete EXIF data, 
            location details, and taxonomic information for serious birders and photographers.
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setFilter(category.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                filter === category.id
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                  : 'glass text-white hover:bg-white/20'
              }`}
            >
              <SafeIcon icon={FiFilter} className="w-4 h-4" />
              <span>{category.name}</span>
              <span className="bg-white/20 px-2 py-1 rounded-full text-xs">
                {category.count}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Gallery Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence>
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-strong rounded-2xl overflow-hidden cursor-pointer group"
                onClick={() => setSelectedImage(image)}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={image.src}
                    alt={image.title}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  
                  {/* Rarity Badge */}
                  <div className="absolute top-4 right-4 glass px-3 py-1 rounded-full flex items-center space-x-1">
                    {renderStars(image.rarity)}
                  </div>

                  {/* Endemic Badge */}
                  {image.category === 'endemic' && (
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-yellow-600 px-3 py-1 rounded-full">
                      <span className="text-white text-xs font-bold">ENDEMIC</span>
                    </div>
                  )}

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Quick Info */}
                  <div className="absolute bottom-4 left-4 right-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
                    <div className="flex items-center space-x-2 text-white text-sm mb-2">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                      <span>{image.location}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-white text-sm">
                      <SafeIcon icon={FiClock} className="w-4 h-4" />
                      <span>{image.time} • {image.date}</span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-white mb-2">
                    {image.title}
                  </h3>
                  <p className="text-gray-300 text-sm italic mb-3">
                    {image.scientificName}
                  </p>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {image.description}
                  </p>
                  
                  {/* Technical Info */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{image.camera}</span>
                      <span>{image.lens}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {image.settings}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Image Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="glass-strong rounded-2xl overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/20">
                  <div>
                    <h3 className="font-display text-2xl font-bold text-white">
                      {selectedImage.title}
                    </h3>
                    <p className="text-gray-300 italic">{selectedImage.scientificName}</p>
                  </div>
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-6 h-6 text-white" />
                  </button>
                </div>

                <div className="flex-1 overflow-auto">
                  <img
                    src={selectedImage.src}
                    alt={selectedImage.title}
                    className="w-full h-auto"
                  />
                  
                  {/* Detailed Info */}
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column */}
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-white font-semibold mb-2 flex items-center">
                            <SafeIcon icon={FiInfo} className="w-4 h-4 mr-2" />
                            Species Information
                          </h4>
                          <p className="text-gray-300 text-sm">{selectedImage.description}</p>
                          <div className="flex items-center space-x-1 mt-2">
                            <span className="text-gray-400 text-sm">Rarity:</span>
                            {renderStars(selectedImage.rarity)}
                          </div>
                        </div>

                        <div>
                          <h4 className="text-white font-semibold mb-2 flex items-center">
                            <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2" />
                            Location & Time
                          </h4>
                          <p className="text-gray-300 text-sm">{selectedImage.location}</p>
                          <p className="text-gray-400 text-sm">{selectedImage.date} at {selectedImage.time}</p>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-white font-semibold mb-2 flex items-center">
                            <SafeIcon icon={FiCamera} className="w-4 h-4 mr-2" />
                            Camera & Lens
                          </h4>
                          <p className="text-gray-300 text-sm">{selectedImage.camera}</p>
                          <p className="text-gray-300 text-sm">{selectedImage.lens}</p>
                        </div>

                        <div>
                          <h4 className="text-white font-semibold mb-2">EXIF Data</h4>
                          <p className="text-gray-300 text-sm font-mono">{selectedImage.settings}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Gallery;